<template>
  <div id="new-license" class="container-fluid">
    <b-form
      id="license-form"
      name="licenseForm"
      class="pt-1 crud-form"
      @submit.prevent="submitLicense">
      <div class="row mt-1">
        <div class="col-4">
          <h3 class="m-0 text-secondary">
            Create License{{this.license.NumberOfLicenses > 1 ? 's' : ''}} for Customer
          </h3>
          <hr class="mt-2">
        </div>
      </div>

      <div class="row">
        <!-- Customer -->
        <div class="col-4">
          <label for="license-customer">
            Customer:
            <small class="text-danger">required</small>
          </label>

          <b-form-select
            id="license-customer"
            name="licenseCustomer"
            size="sm"
            :options="customerOptions"
            v-model="license.CustomerID"
          />
        </div>
        <!-- / Customer -->
      </div>

      <div class="row mt-3">
        <div class="col-4">
          <div class="row">
            <!-- Device Limit -->
            <div class="col-6">
              <label for="license-device-limit">
                Device Limit:
                <small class="text-danger">required</small>
              </label>

              <b-form-input
                id="license-device-limit"
                type="number"
                min="1"
                size="sm"
                name="licenseDeviceLimit"
                placeholder="Enter a device limit"
                v-model="license.DeviceLimit"
              />
            </div>
            <!-- / Device Limit -->

            <!-- Plot Limit -->
            <div class="col-6">
              <label for="license-plot-limit">
                Plot Limit:
                <small class="text-danger">required</small>
              </label>

              <b-form-input
                id="license-plot-limit"
                type="number"
                min="1"
                size="sm"
                name="licensePlotLimit"
                placeholder="Enter a plot limit"
                v-model="license.PlotLimit"
              />
            </div>
            <!-- / Plot Limit -->
          </div>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-4">
          <div class="row">
            <!-- Refresh Interval -->
            <div class="col-6">
              <label for="license-refresh-interval">
                Refresh Interval (days):
                <small class="text-danger">required</small>
              </label>

              <b-form-input
                id="license-refresh-interval"
                type="number"
                min="1"
                size="sm"
                name="licenseRefreshInterval"
                placeholder="Enter a refresh interval"
                v-model="license.RefreshInterval"
              />
            </div>
            <!-- / Refresh Interval -->
          </div>
        </div>
      </div>

      <div class="row mt-3">
        <!-- Features -->


        <div class="col-4">
          <label>
            Features:
          </label>
          <b-form-checkbox-group
            v-model="license.FeatureIDs"
            value-field="ID"
            size="sm"
            text-field="Name"
            :options="features"
          />
        </div>
        <!-- / Features -->
      </div>

      <div class="row mt-3">
        <div class="col-4">
          <div class="row">
            <!-- Starting Date -->
            <div class="col-6">
              <label for="license-starting-date">
                Starting Date:
                <small class="text-danger">required</small>
              </label>

              <b-form-input
                id="license-starting-date"
                type="date"
                size="sm"
                name="licenseStartingDate"
                v-model="license.StartingDate"
              />
            </div>
            <!-- / Starting Date -->

            <!-- Expiration Date -->
            <div class="col-6 pl-0">
              <label for="license-expiration-date">
                Expiration Date:
                <small class="text-danger">required</small>
              </label>

              <b-form-input
                id="license-expiration-date"
                type="date"
                size="sm"
                name="licenseExpirationDate"
                v-model="license.ExpirationDate"
              />
            </div>
            <!-- / Expiration Date -->
          </div>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-4">
          <div class="row">
            <!-- Number Of Licenses -->
            <div class="col-6">
              <label
                for="license-number-of-licenses"
                class="d-flex justify-content-between">
                <div>
                  Number Of Licenses:
                  <small class="text-danger">required</small>
                </div>

                <font-awesome-icon
                  class="float-right"
                  icon="info-circle"
                  v-b-tooltip="{
                    title:'Number of licenses you wish to generate with the above attributes.',
                    placement:'right',
                    delay:{'show':500}
                  }">
                </font-awesome-icon>
              </label>

              <b-form-input
                id="license-number-of-licenses"
                type="number"
                min="1"
                size="sm"
                name="licenseNumberOfLicenses"
                placeholder="Enter number of licenses"
                v-model="license.NumberOfLicenses"
              />
            </div>
            <!-- / Number Of Licenses -->
          </div>
        </div>
      </div>

      <div class="row mt-4 crud-actions">
        <div class="col-4 d-flex justify-content-end">
          <!-- Submit -->
          <b-button
            type="submit"
            size="sm"
            variant="success">
            Save
            <font-awesome-icon icon="save" size="sm"></font-awesome-icon>
          </b-button>
          <!-- / Submit -->

          <!-- Reset -->
          <b-button
            type="button"
            class="ml-3"
            size="sm"
            variant="primary"
            @click="resetLicense">
            Reset
            <font-awesome-icon icon="sync" size="sm"></font-awesome-icon>
          </b-button>
          <!-- / Reset -->
        </div>
      </div>
    </b-form>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import BFormCheckboxGroup from '@delmar/bootstrap-vue/src/components/form-checkbox/form-checkbox-group';
import DataSvc from '../services/data';
import { GlobalLoaderMixin } from '../services/event-bus';
import DirtyFormMixin from '../services/dirty-form';
import mixins from '../services/mixins';

export default {
  name: 'new-license',
  components: { BFormCheckboxGroup },
  mixins: [
    GlobalLoaderMixin,
    DirtyFormMixin,
    mixins,
  ],
  data() {
    const now = moment().startOf('day');
    const l = {
      DeviceLimit: '1',
      PlotLimit: '10000',
      RefreshInterval: '14',
      FeatureIDs: [],
      StartingDate: now.format(moment.HTML5_FMT.DATE),
      ExpirationDate: now.clone().add(1, 'year').format(moment.HTML5_FMT.DATE),
      NumberOfLicenses: '1',
      CustomerID: this.$route.params.ID,
    };

    return {
      defaultLicense: _.cloneDeep(l),
      license: _.cloneDeep(l),
      customers: [],
      features: [],
    };
  },
  computed: {
    isDirty() {
      const licenseJSON = JSON.stringify(this.license);
      const defaultLicenseJSON = JSON.stringify(this.defaultLicense);
      return !_.isEqual(licenseJSON, defaultLicenseJSON);
    },
    customerOptions() {
      const customers = _.chain(this.customers)
        .cloneDeep()
        .map(c => ({
          value: c.ID,
          text: `${c.LastName}, ${c.FirstName}`,
        }))
        .value();

      return [{ text: 'Select a customer' }].concat(customers);
    },
  },
  methods: {
    submitLicense() {
      const l = _.cloneDeep(this.license);

      this.showGlobalLoader(`Creating license${l.NumberOfLicenses > 1 ? 's' : ''}...`);

      return DataSvc.createLicense(l)
        .then(() => {
          this.resetLicense();
          this.$router.push({ name: 'view-customer', params: { ID: l.CustomerID } });
        })
        .finally(() => {
          this.hideGlobalLoader();
        });
    },
    resetLicense() {
      this.license = _.cloneDeep(this.defaultLicense);
    },
    getCustomers() {
      return DataSvc.getCustomers()
        .then((customers) => {
          this.customers = customers;
        });
    },
    getFeatures() {
      return DataSvc.getFeatures()
        .then((features) => {
          this.features = features;
        });
    },
  },
  created() {
    const customersPromise = this.getCustomers();
    const featurePromise = this.getFeatures();

    this.showGlobalLoader('Loading data...');

    Promise.all([
      customersPromise,
      featurePromise,
    ]).finally(() => {
      this.hideGlobalLoader();
    });
  },
};
</script>

<style lang="scss">
</style>
